// ES6 module exporting functions to convert NCS color strings to Hex or RGB

// Updated regex to make the "NCS S " or "NCS " prefix optional
const ncsRe = /^(?:NCS\s?S?\s)?(\d{2})(\d{2})-(N|R|G|B|Y)(\d{2})?(R|G|B|Y)?$/;

const convert = (ncs) => {
    if (typeof ncs !== 'string') return false;
    // Added optional chaining ?. just in case match is null before accessing groups
    // Though the null check below handles it, this adds robustness if logic changes.
    const match = ncs.trim().toUpperCase().match(ncsRe);

    if (match === null) {
        /* console.warn(
            `Invalid NCS format: "${ncs}". Expected format like "NCS S 1080-Y70R" or "1080-Y70R".`
        ); */
        return false;
    }

    // Destructure carefully, group 0 is the full match, group 1 is the prefix (optional)
    // We care about groups starting from the numeric parts
    const [, blacknessStr, chromaticnessStr, hueChar1, huePercentStr, hueChar2] = match;
    const Sn = parseInt(blacknessStr, 10); // Blackness
    const Cn = parseInt(chromaticnessStr, 10); // Chromaticness
    const C1 = hueChar1; // First Hue Character (Y, R, B, G, or N for neutral)
    const N = parseInt(huePercentStr, 10) || 0; // Hue Percentage

    // Check for NaN results from parseInt, indicating regex might have matched unexpectedly
    if (isNaN(Sn) || isNaN(Cn) || !C1) {
        console.error(`Failed to parse NCS components from: "${ncs}"`);
        return false;
    }

    if (C1 === 'N') {
        // Neutral Gray Scale
        const v = Math.round((1 - Sn / 100) * 255);
        return { R: v, G: v, B: v };
    }

    // Chromatic Colors
    const S = 1.05 * Sn - 5.25; // Blackness factor adjustment
    const C = Cn; // Chromaticness factor
    let Ra = 0,
        Ga = 0,
        Ba = 0; // Redness, Greenness, Blueness components (0-1 range initially)

    // --- Calculate Ra (Redness) ---
    if (C1 === 'Y' && N <= 60) {
        Ra = 1;
    } else if ((C1 === 'Y' && N > 60) || (C1 === 'R' && N <= 80)) {
        const x1 = C1 === 'Y' ? N - 60 : N + 40;
        // Clamp the value inside sqrt to prevent NaN for out-of-range N values if logic has gaps
        const radicand = 14884 - x1 ** 2;
        Ra = radicand < 0 ? 0 : (Math.sqrt(radicand) - 22) / 100;
    } else if ((C1 === 'R' && N > 80) || C1 === 'B') {
        Ra = 0;
    } else if (C1 === 'G') {
        const x1 = N - 170;
        const radicand = 33800 - x1 ** 2;
        Ra = radicand < 0 ? 0 : (Math.sqrt(radicand) - 70) / 100;
    }

    // --- Calculate Ba (Blueness) ---
    if (C1 === 'Y' && N <= 80) {
        Ba = 0;
    } else if ((C1 === 'Y' && N > 80) || (C1 === 'R' && N <= 60)) {
        const x2 = C1 === 'Y' ? N - 80 + 20.5 : N + 20 + 20.5;
        const radicand = 11236 - x2 ** 2;
        Ba = radicand < 0 ? 0 : (104 - Math.sqrt(radicand)) / 100;
    } else if ((C1 === 'R' && N > 60) || (C1 === 'B' && N <= 80)) {
        const x3 = C1 === 'R' ? N - 60 - 60 : N + 40 - 60;
        const radicand = 10000 - x3 ** 2;
        Ba = radicand < 0 ? 0 : (Math.sqrt(radicand) - 10) / 100;
    } else if ((C1 === 'B' && N > 80) || (C1 === 'G' && N <= 40)) {
        const x5 = C1 === 'B' ? N - 80 - 131 : N + 20 - 131;
        const radicand = 19881 - x5 ** 2;
        Ba = radicand < 0 ? 0 : (122 - Math.sqrt(radicand)) / 100;
    } else if (C1 === 'G' && N > 40) {
        Ba = 0;
    }

    // --- Calculate Ga (Greenness) ---
    if (C1 === 'Y') {
        Ga = (85 - (17 / 20) * N) / 100;
    } else if (C1 === 'R' && N <= 60) {
        Ga = 0;
    } else if (C1 === 'R' && N > 60) {
        const x6 = N - 60 + 35;
        const radicand = 5776 - x6 ** 2;
        Ga = radicand < 0 ? 0 : (67.5 - Math.sqrt(radicand)) / 100;
    } else if (C1 === 'B' && N <= 60) {
        const x8 = 1 * N - 68.5;
        const radicand = 7044.5 - x8 ** 2;
        Ga = radicand < 0 ? 0 : (6.5 + Math.sqrt(radicand)) / 100;
    } else if ((C1 === 'B' && N > 60) || (C1 === 'G' && N <= 60)) {
        Ga = 0.9;
    } else if (C1 === 'G' && N > 60) {
        const x7 = N - 60;
        Ga = (90 - (1 / 8) * x7) / 100;
    }

    // Clamp intermediate values (Ra, Ga, Ba) before proceeding
    Ra = Math.max(0, Math.min(1, Ra));
    Ga = Math.max(0, Math.min(1, Ga));
    Ba = Math.max(0, Math.min(1, Ba));

    // --- Adjust for Chromaticness (C) ---
    // Mix towards theoretical maximum saturation point for the hue
    const avg = (Ra + Ga + Ba) / 3;
    let Rc = ((avg - Ra) * (100 - C)) / 100 + Ra;
    let Gc = ((avg - Ga) * (100 - C)) / 100 + Ga;
    let Bc = ((avg - Ba) * (100 - C)) / 100 + Ba;

    // --- Adjust for Blackness (S) ---
    // Scale components down based on blackness
    const top = Math.max(Rc, Gc, Bc, 0); // Find the maximum component (should be > 0 for chromatic)

    // Avoid division by zero for pure black or if calculations result in zero top
    const scale = top > 1e-9 ? 1 / top : 0; // Use a small epsilon comparison

    const R_float = ((Rc * scale * (100 - S)) / 100) * 255;
    const G_float = ((Gc * scale * (100 - S)) / 100) * 255;
    const B_float = ((Bc * scale * (100 - S)) / 100) * 255;

    // Clamp final values to 0-255 range and round
    const clamp = (val) => Math.max(0, Math.min(255, Math.round(val)));

    return {
        R: clamp(R_float),
        G: clamp(G_float),
        B: clamp(B_float)
    };
};

// Helper to convert a single decimal value (0-255) to a 2-digit hex string
const hexify = (val) =>
    Math.max(0, Math.min(255, Math.round(val)))
        .toString(16)
        .padStart(2, '0');

// Converts an {R, G, B} object to a #RRGGBB hex string
const toHex = (rgb) => {
    if (
        !rgb ||
        typeof rgb.R === 'undefined' ||
        typeof rgb.G === 'undefined' ||
        typeof rgb.B === 'undefined'
    ) {
        console.error('Invalid RGB object passed to toHex:', rgb);
        return null;
    }
    return `#${hexify(rgb.R)}${hexify(rgb.G)}${hexify(rgb.B)}`;
};

// Converts an {R, G, B} object to an rgb(R,G,B) string
const toRgb = (rgb) => {
    if (
        !rgb ||
        typeof rgb.R === 'undefined' ||
        typeof rgb.G === 'undefined' ||
        typeof rgb.B === 'undefined'
    ) {
        console.error('Invalid RGB object passed to toRgb:', rgb);
        return null;
    }
    // Ensure values are rounded integers for the rgb() string format
    const R = Math.round(rgb.R);
    const G = Math.round(rgb.G);
    const B = Math.round(rgb.B);
    return `rgb(${R},${G},${B})`;
};

// --- Exported Functions ---

const validateNcs = (ncsString) => {
    const rgb = convert(ncsString);
    return rgb ? true : false;
};

/**
 * Converts an NCS string (e.g., "NCS S 1080-Y70R" or "1080-Y70R") to a hex color string.
 * @param {string} ncsString - The NCS color string.
 * @returns {string|null} The hex color string (#RRGGBB) or null if conversion fails.
 */
const ncsToHex = (ncsString) => {
    const rgb = convert(ncsString);
    return rgb ? toHex(rgb) : null;
};

/**
 * Converts an NCS string (e.g., "NCS S 1080-Y70R" or "1080-Y70R") to an rgb color string.
 * @param {string} ncsString - The NCS color string.
 * @returns {string|null} The rgb color string "rgb(R,G,B)" or null if conversion fails.
 */
const ncsToRgb = (ncsString) => {
    const rgb = convert(ncsString);
    return rgb ? toRgb(rgb) : null;
};

/**
 * Converts an NCS string (e.g., "NCS S 1080-Y70R" or "1080-Y70R") to an RGB object.
 * @param {string} ncsString - The NCS color string.
 * @returns {{R: number, G: number, B: number}|false} An object {R, G, B} with values 0-255, or false if conversion fails.
 */
const ncsToRgbObject = (ncsString) => {
    return convert(ncsString); // convert already returns the object or false
};

export { ncsToHex, ncsToRgb, ncsToRgbObject };

// Example Usage (for testing):
// console.log("NCS S 2570-Y80R -> Hex:", ncsToHex("NCS S 2570-Y80R"));
// console.log("2570-Y80R -> Hex:", ncsToHex("2570-Y80R")); // Should now work
// console.log("NCS S 1080-Y70R -> Hex:", ncsToHex("NCS S 1080-Y70R"));
// console.log("NCS S 1000-N -> Hex:", ncsToHex("NCS S 1000-N"));
// console.log("Invalid Input -> Hex:", ncsToHex("Invalid"));
// console.log("2570-Y80R -> RGB Obj:", ncsToRgbObject("2570-Y80R"));
// console.log("2570-Y80R -> RGB Str:", ncsToRgb("2570-Y80R"));
